import React, { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

import { removeFormErrors } from "store/form/actions";

import { useStateSelector } from "utils/field";
import { getOptions, getFirstOption, getBaseUnits, getUnitOptions } from "utils/fields";
import { getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { getContinuousResistivity } from "utils/enclosure/codeLayers";
import { getUdefLayerEmbodiedCarbonDisplay } from "utils/enclosure/embc/udefStandardLayers";

import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Field from "components/Field";
import Accordion from "components/Accordion";
import AccordionActions from "../../AccordionActions";
import { GradeDisplay, PercentageDisplay } from "components/EmbodiedCarbonGrade";
import EmbodiedCarbonEditor from "components/EmbodiedCarbonGrade/UdefEmbodiedCarbonEditor";

import classes from "features/Model/Enclosure/UserDefinedCodes/style.module.scss";

const layerValidation = getValidation("udefContMedLayer");
const categoryValidation = getValidation("udefContMedMaterialCat");
const typeValidation = getValidation("udefContMedMaterialType");
const resistivityValidation = getValidation("udefContMedResistivity");
const thicknessValidation = getValidation("udefContMedThickness");
const rValueValidation = getValidation("udefContMedRValue");

const ContinuousMedium = React.memo(
    ({
        id,
        change,
        accessor,
        drawerAccessor,
        isDragging = false,
        dragHandleProps,
        dragActive = false,
        barMetric = "percentage",
    }) => {
        const dispatch = useDispatch();
        const layer = useStateSelector(`${accessor}`);
        const { id: materialCatId } = useStateSelector(`${accessor}.materialCategory`) || {};
        const { id: materialTypeId } = useStateSelector(`${accessor}.materialType`) || {};
        const resistivityValue = useStateSelector(`${accessor}.resistivity`);
        const thicknessValue = useStateSelector(`${accessor}.thickness`);
        const mediumLayers = useStateSelector(`${drawerAccessor}.uDefCode.layers.continuousMedium`);

        const rValue = parseFloat((resistivityValue * thicknessValue).toFixed(2));

        const resistivityUnits = useStateSelector(`${accessor}.resistivity_u`);
        const thicknessUnits = useStateSelector(`${accessor}.thickness_u`);
        const rValueUnits = useStateSelector(`${accessor}.rValue_u`);
        const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
        const layerIndex = useStateSelector(`${accessor}.layer`);

        const materialTypeOptions = getOptions({ fieldKey: "udefContMedMaterialType", indKey: materialCatId }) || [];

        const { label = "" } = materialTypeOptions.find(({ value: { id = 0 } = {} }) => id === materialTypeId) || {};

        const { displayBase: baseRValUnits = "" } = getBaseUnits("udefContMedRValue", modelUnits);
        const displayRValueUnits = rValueUnits || baseRValUnits;
        const materialLabel = label || `User Defined: ${rValue} ${displayRValueUnits}`;

        const deleteLayer = useCallback(() => {
            const { [id]: _, ...remainingLayers } = mediumLayers;
            change(`${drawerAccessor}.uDefCode.layers.continuousMedium`, remainingLayers);
            dispatch(removeFormErrors(accessor));
        }, [id, mediumLayers, change, dispatch, drawerAccessor, accessor]);

        //ADD USER DEFINED DEPENDENCIES

        const display = useMemo(() => {
            return getUdefLayerEmbodiedCarbonDisplay({ layerType: "continuousMedium", layer });
        }, [layer]);

        const showGrade = barMetric === "grade";

        const gradeInner = (
            <GradeDisplay
                emissions={display.emissions}
                storage={display.storage}
                emissionGrade={display.emissionGrade}
                storageGrade={display.storageGrade}
                withBorder={true}
                borderHeight={"100%"}
                borderColor={"#E1EAF0"}
                customGap={"0.5rem"}
                customPadding={"0 7px"}
                hasMismatch={display.hasMismatch}
            />
        );

        const percentageInner = (
            <PercentageDisplay
                emissions={display.emissions}
                storage={display.storage}
                withBorder={true}
                borderHeight={"100%"}
                borderColor={"#E1EAF0"}
                customGap={"0.5rem"}
                customPadding={"0 7px"}
                hasMismatch={display.hasMismatch}
            />
        );

        return (
            <div>
                <Accordion
                    className={classes.accordion}
                    heading={
                        <span className={classes.accordionHeader}>
                            <span className={classes.accordionPreTitle}>Continuous Medium</span>
                            <span className={classes.accordionTitle}>{materialLabel}</span>
                        </span>
                    }
                    number={layerIndex}
                    isDragging={isDragging}
                    dragActive={dragActive}
                    hoverActions={
                        <AccordionActions
                            dragHandleProps={dragHandleProps}
                            accessor={accessor}
                            deleteLayer={deleteLayer}
                        />
                    }
                    bodyClassName={classes.accordionBodyContent}
                    carbonGrade={
                        <span className={classes.gradeContainer}>{showGrade ? gradeInner : percentageInner}</span>
                    }
                >
                    <div className={classes.inputFields}>
                        <InputRow gridTemplate="1fr">
                            <Field
                                component={Select}
                                name={`${accessor}.materialCategory`}
                                options={getOptions({ fieldKey: "udefContMedMaterialCat" })}
                                label="Material Category"
                                placeholder="Choose Material Category"
                                validate={categoryValidation}
                                onChange={({ id }) => {
                                    if (id === 0) {
                                        change(`${accessor}.materialType`, { id: null });
                                    } else {
                                        change(
                                            `${accessor}.materialType`,
                                            getFirstOption({ indKey: id, depKey: "udefContMedMaterialType" })
                                        );
                                    }
                                }}
                                info={
                                    "Includes any continuous layer that is uninterrupted material. This could include sheathing, masonry, air spaces, or interior or exterior materials."
                                }
                            />
                        </InputRow>
                        <InputRow gridTemplate="2fr 1fr">
                            <Field
                                component={Select}
                                name={`${accessor}.materialType`}
                                options={materialTypeOptions}
                                disabled={materialCatId === 0}
                                label="Material Type"
                                placeholder="Choose Material Type"
                                validate={typeValidation}
                            />
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.resistivity`}
                                label="Resistivity"
                                placeholder={0.0}
                                disabled={materialCatId !== 0}
                                validate={resistivityValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefContMedResistivity")}
                                change={change}
                                setValue={
                                    materialTypeId == null ? resistivityValue : getContinuousResistivity(materialTypeId)
                                }
                                units={{
                                    base: getBaseUnits("udefContMedResistivity", modelUnits),
                                    options: getUnitOptions("thermalResistivity"),
                                    selected: resistivityUnits,
                                    unitType: "thermalResistivity",
                                    accessor: `${accessor}.resistivity_u`,
                                }}
                            />
                        </InputRow>
                        <InputRow gridTemplate="1fr 1fr">
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.thickness`}
                                label="Thickness"
                                placeholder={0.0}
                                validate={thicknessValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefContMedThickness")}
                                change={change}
                                units={{
                                    base: getBaseUnits("udefContMedThickness", modelUnits),
                                    options: getUnitOptions("length"),
                                    selected: thicknessUnits,
                                    unitType: "length",
                                    accessor: `${accessor}.thickness_u`,
                                }}
                            />
                            <Field
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.rValue`}
                                label="R-Value"
                                placeholder={0.0}
                                disabled={true}
                                setValue={rValue}
                                validate={rValueValidation}
                                setTouched={true}
                                decimals={getDecimalPlaces("udefContMedRValue")}
                                change={change}
                                units={{
                                    base: getBaseUnits("udefContMedRValue", modelUnits),
                                    options: getUnitOptions("thermalResistance"),
                                    selected: rValueUnits,
                                    unitType: "thermalResistance",
                                    accessor: `${accessor}.rValue_u`,
                                }}
                            />
                        </InputRow>
                    </div>
                    <div className={classes.embodiedCarbonEditor}>
                        <EmbodiedCarbonEditor
                            change={change}
                            display={display}
                            layersFieldPrefix={accessor}
                            layerType={"continuousMedium"}
                            layer={layer}
                        />
                    </div>
                </Accordion>
            </div>
        );
    }
);

export default ContinuousMedium;
